/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

export interface IStoreSelectorSearchFormProps {
    resources: {
        searchButtonAriaLabel: string;
        searchPlaceholderText: string;
    };

    value: string;
    isBingMapsDisabled?: boolean;

    performSearch(searchTerm: string): Promise<void>;
    searchTermChanged(searchTerm: string): Promise<void>;
}

/**
 * Simple search form consisting of search text and a search button
 */
export class StoreSelectorSearchForm extends React.PureComponent<IStoreSelectorSearchFormProps> {
    constructor(props: IStoreSelectorSearchFormProps) {
        super(props);
        this.state = { value: '' };
    }

    public render(): JSX.Element {
        return (
            <form
                className='ms-store-select__search-form'
                aria-label={this.props.resources.searchButtonAriaLabel}
                name='storeSelectorSearchForm'
                role='form'
                autoComplete='off'
                onSubmit={this._onSubmit}
            >
                <div className='ms-store-select__search-input-container'>
                    <i className='ms-store-select__search-location-icon material-icons-outlined'>location_on</i>
                    <input
                        type='text'
                        className='msc-form-control ms-store-select__search-input'
                        placeholder={this.props.resources.searchPlaceholderText}
                        value={this.props.value}
                        onChange={this._searchTextChanged}
                    />
                    <button
                        className={`ms-store-select__search-button${this.props.isBingMapsDisabled ? ` search-btn-disabled` : ``}`}
                        aria-label={this.props.resources.searchButtonAriaLabel}
                        disabled={this.props.value.length === 0}
                    >
                        <i className='ms-store-select__search-button-icon material-icons'>search</i>
                    </button>
                </div>
                <button className='ms-store-select__submit ms-store-select__search-submit-button' disabled={this.props.value.length === 0}>
                    <span>Search</span>
                </button>
            </form>
        );
    }

    private _searchTextChanged = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const inputValue: string = e.target.value;
        await this.props.searchTermChanged(inputValue);
    };

    private _onSubmit = async (e: React.SyntheticEvent): Promise<void> => {
        e.preventDefault(); // Prevents form submission

        await this.props.performSearch(this.props.value);
    };
}
